import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../context/NotificationContext";
import { useUserRole } from "../../context/UserRoleContext";
import InviteResponseModal from "./InviteResponseModal";
import ShowAcceptDeclineModal from "./ShowAcceptDeclineModal";
import ShowResponseViewModal from "./ShowResponseViewModal";
import { useNotificationNavigation } from "../../hooks/useNotificationNavigation";
import {
  isNotificationGroupActionRequired,
  notificationCountsForBadge,
} from "../../utils/notificationKinds";

let useArtist = null;
try {
  const artistContext = require("../../context/ArtistContext");
  useArtist = artistContext.useArtist;
} catch (e) {
  // optional
}

const NotificationBell = ({ linkOnly = false }) => {
  const {
    groupedNotifications,
    notifications,
    unreadCount,
    markAllAsRead,
  } = useNotifications();
  const { userData } = useUserRole();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  let refreshShows = null;
  let refreshArtists = null;
  if (useArtist && userData?.role === "artist") {
    try {
      const artistContext = useArtist();
      refreshShows = artistContext?.refreshShows;
      refreshArtists = artistContext?.refreshArtists;
    } catch (e) {
      // Not within ArtistProvider
    }
  }

  const {
    handleNotificationClick: navigateFromNotification,
    inviteModal,
    showModal,
    barOwnerModal,
  } = useNotificationNavigation({ refreshShows, refreshArtists });

  const { informationalUnreadCount } = notificationCountsForBadge(
    notifications,
    userData?.role,
  );

  const getBasePath = () =>
    userData?.role === "artist"
      ? "/artist"
      : userData?.role === "bar_owner"
        ? "/bar-owner"
        : "/bar-admin";

  useEffect(() => {
    if (linkOnly) return;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [linkOnly]);

  const handleNotificationClick = (notificationGroup) => {
    navigateFromNotification(notificationGroup);
    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "show_booked":
        return "🎤";
      case "show_cancelled":
        return "❌";
      case "show_updated":
        return "📝";
      case "show_pending":
        return "⏳";
      case "show_accepted":
        return "✅";
      case "show_declined":
        return "❌";
      case "invite_received":
        return "📨";
      case "invite_accepted":
      case "join_request_approved":
        return "✅";
      case "invite_declined":
      case "join_request_rejected":
        return "⛔";
      case "artist_requested":
      case "artist_joined":
      case "artist_left_bar":
        return "🎵";
      case "admin_added":
        return "👤";
      case "admin_removed":
        return "👋";
      default:
        return "🔔";
    }
  };

  const renderNotificationMessage = (notification) => {
    const type = notification?.latestNotification?.type;
    const message =
      notification?.message ?? notification?.latestNotification?.message;
    if (type !== "artist_left_bar" || !message) {
      return <div className="notification-message">{message}</div>;
    }
    const lines = message.split("\n").filter(Boolean);
    const intro = lines[0] || "";
    const showLines = lines.slice(1);
    return (
      <div className="notification-message">
        <p className="notification-artist-left-bar-intro">{intro}</p>
        {showLines.length > 0 && (
          <div className="notification-artist-left-bar-list">
            {showLines.map((line, i) => {
              const parts = line.split(" · ");
              const date = parts[0] || line;
              const time = parts[1] || null;
              return (
                <div key={i} className="notification-artist-left-bar-item">
                  <strong>{date}</strong>
                  {time && (
                    <span className="notification-artist-left-bar-time">
                      {time}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const recentNotifications = groupedNotifications.slice(0, 5);

  return (
    <>
      {inviteModal.isOpen && inviteModal.inviteId && (
        <InviteResponseModal
          isOpen={inviteModal.isOpen}
          onClose={inviteModal.onClose}
          inviteId={inviteModal.inviteId}
          notificationId={inviteModal.notificationId}
          onSuccess={inviteModal.onSuccess}
        />
      )}
      {showModal.isOpen && (showModal.showId || showModal.recurringGroupId) && (
        <ShowAcceptDeclineModal
          isOpen={showModal.isOpen}
          onClose={showModal.onClose}
          showId={showModal.showId}
          recurringGroupId={showModal.recurringGroupId}
          onSuccess={showModal.onSuccess}
        />
      )}
      {barOwnerModal.isOpen && barOwnerModal.recurringGroupId && (
        <ShowResponseViewModal
          isOpen={barOwnerModal.isOpen}
          onClose={barOwnerModal.onClose}
          recurringGroupId={barOwnerModal.recurringGroupId}
        />
      )}
      <div className="notification-bell-container" ref={dropdownRef}>
        <button
          className="notification-bell-button"
          onClick={() =>
            linkOnly
              ? navigate(`${getBasePath()}/notifications`)
              : setIsOpen(!isOpen)
          }
          aria-label="Notifications"
        >
          <span className="bell-icon">🔔</span>
          {unreadCount > 0 && (
            <span className="notification-badge">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {!linkOnly && isOpen && (
          <div className="notification-dropdown">
            <div className="notification-dropdown-header">
              <h3>Notifications</h3>
              {informationalUnreadCount > 0 && (
                <button
                  className="mark-all-read-btn"
                  onClick={() => {
                    markAllAsRead();
                    setIsOpen(false);
                  }}
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="notification-list">
              {recentNotifications.length === 0 ? (
                <div className="no-notifications">
                  <span className="no-notif-icon">🔕</span>
                  <p>No notifications yet</p>
                </div>
              ) : (
                recentNotifications.map((notification) => {
                  const actionable = isNotificationGroupActionRequired(
                    notification,
                    userData?.role,
                  );
                  return (
                    <div
                      key={notification.groupId}
                      className={`notification-item ${
                        notification.unreadCount > 0 ? "unread" : ""
                      } ${actionable ? "notification-item--action-required" : ""}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-icon">
                        {getNotificationIcon(
                          notification.latestNotification.type,
                        )}
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">
                          {notification.title}
                        </div>
                        {actionable && (
                          <span className="notification-dropdown-action-badge">
                            Action required
                          </span>
                        )}
                        {renderNotificationMessage(notification)}
                        <div className="notification-time">
                          {formatTimeAgo(notification.latestCreatedAt)}
                        </div>
                      </div>
                      {notification.unreadCount > 0 && (
                        <div className="unread-indicator"></div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {groupedNotifications.length > 5 && (
              <div className="notification-dropdown-footer">
                <button
                  className="view-all-btn"
                  onClick={() => {
                    const basePath =
                      userData?.role === "artist"
                        ? "/artist"
                        : userData?.role === "bar_owner"
                          ? "/bar-owner"
                          : "/bar-admin";
                    navigate(`${basePath}/notifications`);
                    setIsOpen(false);
                  }}
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationBell;
